import { usePredictions } from "@/hooks/usePredictions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldAlert, AlertCircle } from "lucide-react";
interface GameDisciplineProps {
  fixtureId: number | null;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
}
export function GameDiscipline({
  fixtureId,
  homeTeamName,
  awayTeamName,
  homeTeamLogo,
  awayTeamLogo
}: GameDisciplineProps) {
  const {
    data,
    isLoading
  } = usePredictions(fixtureId);

  // Parse cards by interval (amarelos + vermelhos)
  const parseCardsByInterval = (homeStats: any, awayStats: any) => {
    if (!homeStats || !awayStats) return {
      home: [],
      away: []
    };
    const intervals = ['0-15', '16-30', '31-45', '46-60', '61-75', '76-90'];
    return {
      home: intervals.map(interval => ({
        interval,
        yellowCards: homeStats.yellow_cards_by_minute?.[interval] || 0,
        redCards: homeStats.red_cards_by_minute?.[interval] || 0
      })),
      away: intervals.map(interval => ({
        interval,
        yellowCards: awayStats.yellow_cards_by_minute?.[interval] || 0,
        redCards: awayStats.red_cards_by_minute?.[interval] || 0
      }))
    };
  };

  // Helper for card interval background color (menos cartões = verde)
  const getCardIntervalBgColor = (yellow: number, red: number) => {
    const total = yellow + red;
    if (total === 0) return 'bg-green-50'; // No cards = excellent
    if (total === 1) return 'bg-yellow-50'; // Few cards = ok
    return 'bg-red-50'; // Many cards = concerning
  };

  // Calcular total de cartões (somar todos os intervalos)
  const calculateTotalCards = (cardsByMinute: any): number => {
    if (!cardsByMinute) return 0;
    return Object.values(cardsByMinute).reduce<number>((sum: number, val: any) => {
      return sum + (Number(val) || 0);
    }, 0);
  };

  // Calcular estatísticas de cartões
  const calculateCardStats = (teamStats: any) => {
    const totalYellow: number = calculateTotalCards(teamStats.yellow_cards_by_minute);
    const totalRed: number = calculateTotalCards(teamStats.red_cards_by_minute);
    const totalCards: number = totalYellow + totalRed;
    const gamesPlayed: number = teamStats.league_fixtures_played_total || 0;
    const avgCards: number = gamesPlayed > 0 ? totalCards / gamesPlayed : 0;
    return {
      totalYellow,
      totalRed,
      totalCards,
      avgCards,
      gamesPlayed
    };
  };
  if (isLoading) {
    return <div className="space-y-4">
        <Skeleton className="h-96 w-full" />
      </div>;
  }
  if (!data || !data.teamStats.home || !data.teamStats.away) {
    return <div className="text-center py-12">
        <div className="sketch-border bg-paper-sketch p-8 max-w-md mx-auto">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-medium" />
          <h3 className="text-base md:text-sketch-md font-black mb-3">
            Discipline Data Not Available
          </h3>
          <p className="text-sm text-gray-medium">
            Card data has not been generated for this game yet.
          </p>
        </div>
      </div>;
  }
  const {
    teamStats
  } = data;
  const cardsByInterval = parseCardsByInterval(teamStats.home, teamStats.away);

  // Calcular estatísticas de cartões para ambas as equipas
  const homeStats = calculateCardStats(teamStats.home);
  const awayStats = calculateCardStats(teamStats.away);

  // Calculate max cards for scaling bars
  const maxCardsInIntervals = Math.max(1, ...cardsByInterval.home.flatMap(i => [i.yellowCards, i.redCards]), ...cardsByInterval.away.flatMap(i => [i.yellowCards, i.redCards]));
  return <div className="space-y-6">
      {/* Momento dos Cartões */}
      <Card className="sketch-border bg-card overflow-hidden">
        <CardHeader>
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 text-yellow-600" />
            <CardTitle className="text-sketch-md">Cards Timing</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {/* Estatísticas Gerais de Cartões */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Estatísticas Casa */}
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-4 rounded-lg sketch-border overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-5 h-5" />}
                <span className="font-black text-sm truncate">{homeTeamName}</span>
              </div>
              
              {/* Total de Cartões */}
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs text-muted-foreground">Total:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold">🟨 {Number(homeStats.totalYellow)}</span>
                  <span className="text-sm font-bold">🟥 {Number(homeStats.totalRed)}</span>
                </div>
              </div>
              
              {/* Média por Jogo */}
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-xs text-muted-foreground">Avg:</span>
                <span className="text-lg font-black text-primary leading-tight">
                  {homeStats.avgCards.toFixed(2)}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">cards/game</span>
              </div>
              
              {/* Número de Jogos */}
              <div className="text-[10px] text-muted-foreground/70 mt-2">
                {homeStats.gamesPlayed} games played
              </div>
            </div>

            {/* Estatísticas Fora */}
            <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 p-4 rounded-lg sketch-border overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-5 h-5" />}
                <span className="font-black text-sm truncate">{awayTeamName}</span>
              </div>
              
              {/* Total de Cartões */}
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs text-muted-foreground">Total:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold">🟨 {Number(awayStats.totalYellow)}</span>
                  <span className="text-sm font-bold">🟥 {Number(awayStats.totalRed)}</span>
                </div>
              </div>
              
              {/* Média por Jogo */}
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-xs text-muted-foreground">Avg:</span>
                <span className="text-lg font-black text-foreground leading-tight">
                  {awayStats.avgCards.toFixed(2)}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">cards/game</span>
              </div>
              
              {/* Número de Jogos */}
              <div className="text-[10px] text-muted-foreground/70 mt-2">
                {awayStats.gamesPlayed} games played
              </div>
            </div>
          </div>

          {/* Separador visual */}
          <div className="border-t border-dashed border-border mb-6" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Coluna Casa */}
            <div className="space-y-3">
              {/* Header Casa */}
              <div className="flex items-center justify-center gap-2 pb-2 border-b">
                {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-5 h-5" />}
                <span className="font-black text-sm">{homeTeamName}</span>
              </div>

              {/* Intervalos Casa */}
              {cardsByInterval.home.map(interval => <div key={`home-cards-${interval.interval}`} className={`${getCardIntervalBgColor(interval.yellowCards, interval.redCards)} p-3 rounded-lg space-y-2`}>
                  {/* Label do Intervalo */}
                  <div className="font-black text-sm text-foreground">{interval.interval}'</div>
                  
                  {/* Amarelos - Barra Amarela */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-14 text-muted-foreground">🟨 Yel</span>
                    <span className="text-xs font-black w-6 text-foreground">{interval.yellowCards}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-500 transition-all" style={{
                    width: `${interval.yellowCards / maxCardsInIntervals * 100}%`
                  }} />
                    </div>
                  </div>
                  
                  {/* Vermelhos - Barra Vermelha */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-14 text-muted-foreground">🟥 Red</span>
                    <span className="text-xs font-black w-6 text-foreground">{interval.redCards}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-red-600 transition-all" style={{
                    width: `${interval.redCards / maxCardsInIntervals * 100}%`
                  }} />
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Coluna Fora */}
            <div className="space-y-3">
              {/* Header Fora */}
              <div className="flex items-center justify-center gap-2 pb-2 border-b">
                {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-5 h-5" />}
                <span className="font-black text-sm">{awayTeamName}</span>
              </div>

              {/* Intervalos Fora */}
              {cardsByInterval.away.map(interval => <div key={`away-cards-${interval.interval}`} className={`${getCardIntervalBgColor(interval.yellowCards, interval.redCards)} p-3 rounded-lg space-y-2`}>
                  {/* Label do Intervalo */}
                  <div className="font-black text-sm text-foreground">{interval.interval}'</div>
                  
                  {/* Amarelos - Barra Amarela */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-14 text-muted-foreground">🟨 Yel</span>
                    <span className="text-xs font-black w-6 text-foreground">{interval.yellowCards}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-500 transition-all" style={{
                    width: `${interval.yellowCards / maxCardsInIntervals * 100}%`
                  }} />
                    </div>
                  </div>
                  
                  {/* Vermelhos - Barra Vermelha */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-14 text-muted-foreground">🟥 Red</span>
                    <span className="text-xs font-black w-6 text-foreground">{interval.redCards}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-red-600 transition-all" style={{
                    width: `${interval.redCards / maxCardsInIntervals * 100}%`
                  }} />
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Nota sobre descontos */}
          <p className="text-xs text-muted-foreground text-center italic mt-4">
            * Periods 31-45 and 76-90 include injury time
          </p>
        </CardContent>
      </Card>
    </div>;
}